import { useCallback, useEffect, useRef, useState, type TouchEvent } from "react";
import { useNavigate } from "react-router-dom";
import { bannerItems } from "@/mocks/videos";
import { getVideoById } from "@/lib/videoService";

export default function HeroCarousel() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const slides = bannerItems
    .map((banner) => {
      const video = getVideoById(banner.id);
      return video ? { ...banner, video } : null;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  const total = slides.length;

  const go = useCallback(
    (next: number) => {
      setIndex(((next % total) + total) % total);
    },
    [total]
  );

  useEffect(() => {
    const timer = setInterval(() => setIndex((prev) => (prev + 1) % total), 6000);
    return () => clearInterval(timer);
  }, [total]);

  const onTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(index + (delta < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  const active = slides[index];

  return (
    <section
      className="relative w-full h-[560px] md:h-[660px] overflow-hidden bg-foreground-950"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="热门推荐轮播"
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0"}`}
          aria-hidden={i !== index}
        >
          <img
            src={slide.video.banner}
            alt={`${slide.title}焦点图`}
            title={`${slide.title} - 芭乐视频热门推荐`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/55 to-foreground-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/30" />

      <div className="relative z-10 w-full h-full px-4 md:px-8 flex items-center">
        <div className="max-w-xl animate-fade-up" key={active?.id}>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-label font-semibold">
            <i className="ri-fire-fill" /> {active?.tag}
          </span>
          <h2 className="mt-5 font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight">
            {active?.title}
          </h2>
          <p className="mt-4 text-sm md:text-base text-background-100/85 leading-relaxed max-w-lg">
            {active?.desc}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => active && navigate(`/detail/${active.id}`)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-play-fill" /> 查看详情
            </button>
            <button
              type="button"
              onClick={() => active && navigate(`/category/${active.video.type}`)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-background-50/25 transition-colors backdrop-blur-sm"
            >
              <i className="ri-compass-3-line" /> 探索更多{active?.video.type}
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-0 right-0 z-10 px-4 md:px-8 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => go(i)}
              className={`h-1.5 rounded-full transition-all ${i === index ? "w-8 bg-primary-500" : "w-4 bg-background-50/40 hover:bg-background-50/70"}`}
              aria-label={`切换到第${i + 1}张`}
            />
          ))}
        </div>
        <div className="hidden md:flex items-center gap-2">
          <button
            type="button"
            onClick={() => go(index - 1)}
            className="flex items-center justify-center w-10 h-10 rounded-md bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors backdrop-blur-sm"
            aria-label="上一张"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            className="flex items-center justify-center w-10 h-10 rounded-md bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors backdrop-blur-sm"
            aria-label="下一张"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </div>
    </section>
  );
}